/**
 * DashboardSidebar — the left navigation column for every OL role.
 *
 * Geometry matches BackOfficePro's shell (`BOP2 frontend/src/components/
 * Layout.js` + `Sidebar.js`): a FIXED full-height column pinned top-left with
 * the brand inside it, and the page header living in the content column to its
 * right. Nothing sits above the sidebar. OL previously stacked two full-width
 * rows above a boxed sidebar, which is what AJ's screenshot flagged.
 *
 *   <div>                        min-h-screen
 *     <DashboardSidebar/>        fixed top-0 left-0 h-screen  (w-56 / w-14)
 *     <div className="md:ml-56"> header (user upper right), banners, main
 *
 * IT INVENTS NO VISIBILITY RULES. The caller passes the already-filtered list,
 * and this component asks `entitlements` the same two questions the tab strip
 * it replaced asked:
 *
 *   isVisible(gate)     — show it at all
 *   isRestricted(gate)  — show it greyed, as a shop window
 *
 * so the sidebar, the page catalog and `/api/view-config/my-pages` all still
 * resolve from one place (`services/entitlements.py`). See
 * `docs/OL_ENTITLEMENTS.md` §5b.
 *
 * ORDER comes from `config/pageRegistry.js` — the array index is the order, as
 * everywhere else. Sections follow SECTION_ORDER; a category not listed falls
 * to the bottom rather than disappearing.
 *
 * BEHAVIOUR (AJ 2026-10-19) = BackOfficePro's sidebar: one row per category with
 * a right caret, pages in a hover / click flyout, single-page categories as plain
 * links, and BOP's round ‹ / › edge button to shrink to icons (`CategoryNav`).
 * The phone drawer keeps the full list (no hover on a touch screen).
 */
import React, { useMemo, useRef, useState, useCallback } from 'react';
import {
  Home, Users, CalendarDays, ClipboardList, Trophy, Megaphone, Image as ImageIcon,
  Globe, BarChart3, DollarSign, HeartHandshake, Wrench, Settings as SettingsIcon,
  Shield, UserSquare2, Swords, Lock, ChevronLeft, ChevronRight, X, BookOpen, Wallet,
} from 'lucide-react';
import { PAGE_META, pageLabel, ROLE_NAV_ORDER } from '../config/pageRegistry';

/** A club's day runs roughly top to bottom; Tools, Settings and the
 *  Semprion-only Platform pages sink to the end because they are visited rarely
 *  and should never push daily work down the list. */
const SECTION_ORDER = [
  'Home', 'Teams', 'Scheduling', 'Game Day', 'Registration', 'Members',
  'Communications', 'Media', 'Website', 'Statistics', 'Library', 'Finance', 'AD Finance',
  'Tournaments', 'Volunteer', 'Tools', 'Settings', 'Platform',
];

const SECTION_ICON = {
  Home, Teams: Users, Scheduling: CalendarDays, 'Game Day': Swords,
  Registration: ClipboardList, Members: UserSquare2, Communications: Megaphone,
  Media: ImageIcon, Website: Globe, Statistics: BarChart3, Finance: DollarSign, 'AD Finance': Wallet,
  Tournaments: Trophy, Volunteer: HeartHandshake, Tools: Wrench, Library: BookOpen,
  Settings: SettingsIcon, Platform: Shield,
};

/** `Home` holds the landing page for every role and reads as noise above it. */
const UNLABELLED_SECTIONS = new Set(['Home']);

/** The menu itself, shared by the fixed desktop column and the mobile drawer so
 *  the two can never drift apart. */
function NavList({ sections, activeTab, onSelect, entitlements, primaryRole, iconOnly }) {
  return (
    <div className="flex-1 overflow-y-auto py-1">
      {sections.map(({ category, pages }) => {
        const SectionIcon = SECTION_ICON[category] || Home;
        const labelled = !iconOnly && !UNLABELLED_SECTIONS.has(category);
        return (
          <div key={category} className="mb-1">
            {labelled && (
              <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                {category}
              </div>
            )}
            {iconOnly && !UNLABELLED_SECTIONS.has(category) && (
              <div className="mx-2 my-1.5 border-t border-white/10" />
            )}

            {pages.map((item) => {
              const gate = entitlements.gateFor({ ...item, ...(PAGE_META[item.value] || {}) });
              // `restrict` keeps the page listed but greyed with a lock — a
              // customer seeing what they have not bought is what sells an
              // upgrade. The panel itself still refuses to do anything useful.
              const restricted = entitlements.isRestricted(gate);
              const active = activeTab === item.value;
              const label = pageLabel(item, primaryRole);

              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => onSelect(item.value)}
                  data-testid={item.testid || `nav-${item.value}`}
                  title={`${label}${restricted ? ' — not included in your current plan' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors
                              ${active
                                ? 'bg-white/10 text-white font-medium border-l-2 border-amber-500'
                                : 'text-slate-300 hover:bg-white/5 hover:text-white border-l-2 border-transparent'}
                              ${restricted ? 'opacity-50' : ''}`}
                >
                  <SectionIcon className="h-4 w-4 shrink-0" />
                  {!iconOnly && (
                    <>
                      {/* min-w-0 is required or truncate collapses the label to
                          a single character inside a flex row. */}
                      <span className="flex-1 min-w-0 truncate">{label}</span>
                      {restricted && <Lock className="h-3 w-3 shrink-0 text-slate-400" />}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

/** Desktop menu — behaves like BackOfficePro's sidebar (AJ 2026-10-19; `BOP2
 *  frontend/src/components/Sidebar.js`): ONE ROW PER CATEGORY, closed by default,
 *  with a right caret (›). Hovering a row (or clicking it) opens a flyout beside
 *  the sidebar listing that category's pages. A category with a single page is a
 *  plain link — no caret, nothing to open — exactly as BOP renders its `link`
 *  sections. The row of the open page's category is highlighted. Visibility and
 *  the restricted/lock state are the same entitlement answers as before. */
function CategoryNav({ sections, activeTab, onSelect, entitlements, primaryRole, iconOnly }) {
  const [open, setOpen] = useState(null);          // { category, top }
  const closeTimer = useRef(null);
  const cancelClose = useCallback(() => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);
  const closeSoon = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(null), 150);
  }, [cancelClose]);
  const openFor = (category, e) => {
    cancelClose();
    setOpen({ category, top: e.currentTarget.getBoundingClientRect().top });
  };

  const pageState = (item) => {
    const gate = entitlements.gateFor({ ...item, ...(PAGE_META[item.value] || {}) });
    return { restricted: entitlements.isRestricted(gate), label: pageLabel(item, primaryRole) };
  };
  const rowCls = (active, restricted) => `w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-left transition-colors
    ${iconOnly ? 'justify-center' : ''}
    ${active ? 'bg-zinc-800 text-white' : 'text-slate-300 hover:bg-zinc-700 hover:text-white'}
    ${restricted ? 'opacity-50' : ''}`;

  const flyoutSection = open && sections.find(sc => sc.category === open.category);
  const flyTop = open ? Math.max(8, Math.min(open.top, (typeof window !== 'undefined' ? window.innerHeight : 900) - 320)) : 0;

  return (
    <>
      <nav className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="sidebar-categories">
        {sections.map(({ category, pages }) => {
          const Icon = SECTION_ICON[category] || Home;
          // One page → a direct link, labelled with the page (e.g. "Schedule").
          if (pages.length === 1) {
            const item = pages[0];
            const { restricted, label } = pageState(item);
            return (
              <button key={category} type="button" onClick={() => onSelect(item.value)}
                data-testid={item.testid || `nav-${item.value}`}
                title={`${label}${restricted ? ' — not included in your current plan' : ''}`}
                aria-current={activeTab === item.value ? 'page' : undefined}
                className={rowCls(activeTab === item.value, restricted)}>
                <Icon className="h-5 w-5 shrink-0" />
                {!iconOnly && <span className="flex-1 min-w-0 truncate font-medium">{label}</span>}
                {!iconOnly && restricted && <Lock className="h-3 w-3 shrink-0 text-slate-400" />}
              </button>
            );
          }
          const active = pages.some(pg => pg.value === activeTab) || open?.category === category;
          return (
            <button key={category} type="button"
              onMouseEnter={(e) => openFor(category, e)} onMouseLeave={closeSoon}
              onClick={(e) => (open?.category === category ? setOpen(null) : openFor(category, e))}
              data-testid={`nav-section-${category.toLowerCase().replace(/\s+/g, '-')}`}
              title={iconOnly ? category : ''} aria-haspopup="menu" aria-expanded={open?.category === category}
              className={`${rowCls(active, false)} ${iconOnly ? '' : 'justify-between'}`}>
              <span className="flex items-center gap-3 min-w-0">
                <Icon className="h-5 w-5 shrink-0" />
                {!iconOnly && <span className="truncate font-medium">{category}</span>}
              </span>
              {!iconOnly && <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" />}
            </button>
          );
        })}
      </nav>

      {flyoutSection && (
        <div role="menu" data-testid={`nav-flyout-${open.category.toLowerCase().replace(/\s+/g, '-')}`}
          onMouseEnter={cancelClose} onMouseLeave={closeSoon}
          className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[220px] max-w-[280px] flex flex-col overflow-hidden"
          style={{ left: iconOnly ? 56 : 224, top: flyTop, maxHeight: `calc(100vh - ${flyTop}px - 12px)` }}>
          <div className="px-3 py-2 border-b border-gray-100 mb-1 font-semibold text-gray-900">{open.category}</div>
          <div className="flex-1 min-h-0 overflow-y-auto px-1">
            {flyoutSection.pages.map((item) => {
              const { restricted, label } = pageState(item);
              const Icon = SECTION_ICON[open.category] || Home;
              const active = activeTab === item.value;
              return (
                <button key={item.value} type="button" role="menuitem"
                  onClick={() => { setOpen(null); onSelect(item.value); }}
                  data-testid={item.testid || `nav-${item.value}`}
                  title={restricted ? 'Not included in your current plan' : label}
                  aria-current={active ? 'page' : undefined}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors
                    ${active ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}
                    ${restricted ? 'opacity-50' : ''}`}>
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 min-w-0 truncate">{label}</span>
                  {restricted && <Lock className="h-3 w-3 shrink-0 text-gray-400" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

/** The PLATFORM brand, exactly where BOP puts "BackOfficePro / TRIAL".
 *
 *  AJ, 2026-10-18: *"Org logo does not go in left sidebar next to Omnileague.
 *  Semprion logo (same as BOP) with org logo at top lone similar to Taco
 *  Roos."* So the vendor brand is here and the CUSTOMER's logo sits in the page
 *  header. `public/semprion_logo.png` is BOP's own file, copied over — Semprion
 *  owns both products, so it is literally the same mark.
 *
 *  Markup and classes follow `BOP2 frontend/src/components/Sidebar.js`, and the
 *  BADGE follows BOP's UI_GUIDE §3: customer roles show the SUBSCRIPTION level,
 *  the platform operator shows nothing, and it is NEVER the role's name (that
 *  was BOP's Bug #32).
 */
function Brand({ badge, iconOnly, onClose }) {
  return (
    <div className={`border-b border-gray-600 ${iconOnly ? 'px-3 py-4' : 'p-4'}`}>
      <div className="flex items-center gap-3">
        <img
          src="/semprion_logo.png"
          alt="OmniLeagues"
          className="w-9 h-9 object-contain flex-shrink-0"
          data-testid="app-logo"
        />
        {!iconOnly && (
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-base font-black tracking-tight truncate">OmniLeagues</span>
            {badge && (
              <span className="text-[10px] text-blue-400 uppercase tracking-wider font-semibold truncate"
                    data-testid="sidebar-role-badge">
                {badge}
              </span>
            )}
          </div>
        )}
        {onClose && (
          <button type="button" onClick={onClose} data-testid="sidebar-close-mobile"
            className="text-slate-400 hover:text-white p-1" title="Close menu">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function DashboardSidebar({
  items, activeTab, onSelect, entitlements, primaryRole,
  collapsed = false, onToggleCollapse,
  badge,
  mobileOpen = false, onCloseMobile,
}) {
  const sections = useMemo(() => {
    const byCategory = new Map();
    items.forEach((item) => {
      const category = PAGE_META[item.value]?.category || 'Home';
      if (!byCategory.has(category)) byCategory.set(category, []);
      byCategory.get(category).push(item);
    });
    // A role with its own menu order (ROLE_NAV_ORDER) keeps the order the items
    // arrive in; every other role sorts its sections by SECTION_ORDER.
    const rank = ROLE_NAV_ORDER[primaryRole]
      ? (c) => items.findIndex(i => (PAGE_META[i.value]?.category || 'Home') === c)
      : (c) => {
        const i = SECTION_ORDER.indexOf(c);
        return i === -1 ? SECTION_ORDER.length : i;
      };
    return [...byCategory.entries()]
      .sort((a, b) => rank(a[0]) - rank(b[0]))
      .map(([category, pages]) => ({ category, pages }));
  }, [items, primaryRole]);

  const listProps = { sections, activeTab, entitlements, primaryRole };

  return (
    <>
      {/* Desktop: the fixed full-height column. */}
      <aside
        aria-label="Main"
        data-testid="dashboard-sidebar"
        className={`hidden md:flex flex-col fixed top-0 left-0 h-screen z-40
                    bg-[#2D3748] text-white transition-all duration-300
                    ${collapsed ? 'w-14' : 'w-56'}`}
      >
        {/* Collapse toggle — BOP's round edge button (‹ / ›), not a footer row. */}
        <button
          type="button"
          onClick={onToggleCollapse}
          data-testid="sidebar-collapse"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className="absolute -right-3 top-20 z-10 w-6 h-6 bg-gray-600 hover:bg-gray-500 rounded-full
                     flex items-center justify-center text-gray-300 hover:text-white transition-colors shadow-lg"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
        <Brand badge={badge} iconOnly={collapsed} />
        <CategoryNav {...listProps} onSelect={onSelect} iconOnly={collapsed} />
      </aside>

      {/* Mobile: the same menu as a drawer, so a phone is not left without
          navigation once the fixed column is hidden. */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="sidebar-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={onCloseMobile} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-[#2D3748] text-white
                            flex flex-col shadow-xl">
            <Brand badge={badge} onClose={onCloseMobile} />
            <NavList {...listProps} iconOnly={false}
              onSelect={(v) => { onSelect(v); onCloseMobile?.(); }} />
          </aside>
        </div>
      )}
    </>
  );
}
